import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Vocabulary shared with backend/exercise_meta.py
export const PATTERN_LABELS = {
  squat: 'Przysiad', hinge: 'Zawias biodrowy', lunge: 'Wykrok / jednonóż',
  push_h: 'Pchanie poziome', push_v: 'Pchanie pionowe', pull_h: 'Ciągnięcie poziome', pull_v: 'Ciągnięcie pionowe',
  carry: 'Noszenie', core_antiext: 'Tułów: anty-wyprost', core_antirot: 'Tułów: anty-rotacja', core_lateral: 'Tułów: boczny',
  calf: 'Łydki', isolation_arm: 'Izolacja: ramiona', isolation_shoulder: 'Izolacja: barki', isolation_chest: 'Izolacja: klatka',
  isolation_leg: 'Izolacja: nogi', activation: 'Aktywacja', mobility: 'Mobilność', warmup: 'Rozgrzewka',
  cardio: 'Cardio', plyometric: 'Skoki', balance: 'Równowaga',
};
export const LOAD_TAG_LABELS = {
  loaded_flexion: 'Obciążony skłon', axial_load: 'Obciążenie osiowe', overhead: 'Praca nad głową',
  impact: 'Impakt (skoki)', long_flexion_iso: 'Długa izometria w zgięciu', loaded_rotation: 'Rotacja pod obciążeniem',
};
export const REGION_LABELS = {
  neck: 'Kark', shoulder: 'Bark', elbow_wrist: 'Łokieć / nadgarstek', lumbar: 'Dolne plecy',
  hip: 'Biodro', knee: 'Kolano', ankle: 'Kostka',
};
export const EQUIPMENT_LABELS = {
  none: 'Bez sprzętu', mat: 'Mata', bands: 'Gumy', dumbbells: 'Hantle', kettlebell: 'Kettlebell', barbell: 'Sztanga',
  pullup_bar: 'Drążek', bench: 'Ławka', box: 'Skrzynia / stopień', cable: 'Wyciąg', machine: 'Maszyna', foam_roller: 'Wałek',
};
const LEVELS = [[0, '0 · najłatwiejszy'], [1, '1 · średni'], [2, '2 · najtrudniejszy']];

const chip = (on) => `text-xs px-3 py-1 rounded-full border ${on ? 'border-[#C9A84C] text-[#C9A84C] bg-[#C9A84C]/10' : 'border-[#2A2A2A] text-[#A1A1A1]'}`;
const selectCls = 'w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2 text-sm text-white';
const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

// Trainer's form for the plan-generator fields of one exercise. Saving marks
// the exercise as reviewed (meta_reviewed), so the proposals from the
// automatic backfill drop off the "Do przejrzenia" list.
export const ExerciseMetaForm = ({ exercise, allExercises = [], onSaved }) => {
  const [form, setForm] = useState(() => ({
    pattern: exercise.pattern || '',
    level: exercise.level ?? 0,
    load_tags: exercise.load_tags || [],
    risk: Object.fromEntries((exercise.risk_regions || []).map((r) => [r.region, r.level])),
    self_service: exercise.self_service ?? true,
    equipment: exercise.equipment || [],
    regression_of: exercise.regression_of || '',
    progression_of: exercise.progression_of || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const others = allExercises.filter((e) => e.id !== exercise.id && (!form.pattern || e.pattern === form.pattern));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        pattern: form.pattern || null,
        level: Number(form.level),
        load_tags: form.load_tags,
        risk_regions: Object.entries(form.risk).filter(([, lvl]) => lvl).map(([region, level]) => ({ region, level })),
        self_service: form.self_service,
        equipment: form.equipment,
        regression_of: form.regression_of,
        progression_of: form.progression_of,
        meta_reviewed: true,
      };
      const res = await axios.put(`${API_URL}/api/exercises/${exercise.id}`, payload, { withCredentials: true });
      toast.success('Zapisano dane dla generatora');
      onSaved?.(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nie udało się zapisać');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="exercise-meta-form">
      <div className="flex items-center justify-between gap-2">
        <label className="label-text text-[#A1A1A1]">DANE DLA GENERATORA PLANU</label>
        {!exercise.meta_reviewed && <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">propozycja automatyczna</span>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-[#A1A1A1] space-y-1">
          <span>Wzorzec ruchu</span>
          <select className={selectCls} value={form.pattern} onChange={(e) => set({ pattern: e.target.value })} data-testid="meta-pattern">
            <option value="">— brak —</option>
            {Object.entries(PATTERN_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="text-xs text-[#A1A1A1] space-y-1">
          <span>Poziom wariantu</span>
          <select className={selectCls} value={form.level} onChange={(e) => set({ level: e.target.value })} data-testid="meta-level">
            {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>

      <div className="space-y-2">
        <div className="text-xs text-[#A1A1A1]">Sprzęt</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(EQUIPMENT_LABELS).map(([v, l]) => (
            <button key={v} type="button" className={chip(form.equipment.includes(v))} onClick={() => set({ equipment: toggle(form.equipment, v) })}>{l}</button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs text-[#A1A1A1]">Rodzaj obciążenia (wyklucza ćwiczenie przy trudnej czynności)</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(LOAD_TAG_LABELS).map(([v, l]) => (
            <button key={v} type="button" className={chip(form.load_tags.includes(v))} onClick={() => set({ load_tags: toggle(form.load_tags, v) })}>{l}</button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs text-[#A1A1A1]">Okolica: „ostrożnie” = pomijane przy umiarkowanych dolegliwościach, „unikaj” = zawsze pomijane</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Object.entries(REGION_LABELS).map(([region, label]) => (
            <div key={region} className="flex items-center justify-between gap-2 text-sm text-white">
              <span>{label}</span>
              <div className="flex gap-1">
                {[['', '—'], ['caution', 'ostrożnie'], ['avoid', 'unikaj']].map(([lvl, l]) => (
                  <button key={lvl} type="button" className={chip((form.risk[region] || '') === lvl)}
                    onClick={() => set({ risk: { ...form.risk, [region]: lvl } })}>{l}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-[#A1A1A1] space-y-1">
          <span>Jest łatwiejszą wersją (regresją) ćwiczenia</span>
          <select className={selectCls} value={form.regression_of} onChange={(e) => set({ regression_of: e.target.value })} data-testid="meta-regression">
            <option value="">— brak —</option>
            {others.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-[#A1A1A1] space-y-1">
          <span>Jest trudniejszą wersją (progresją) ćwiczenia</span>
          <select className={selectCls} value={form.progression_of} onChange={(e) => set({ progression_of: e.target.value })} data-testid="meta-progression">
            <option value="">— brak —</option>
            {others.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
      </div>

      <label className="flex items-center gap-2 text-sm text-white">
        <input type="checkbox" checked={form.self_service} onChange={(e) => set({ self_service: e.target.checked })} data-testid="meta-self-service" />
        Automat może wybrać to ćwiczenie dla klienta samodzielnego
      </label>

      <Button onClick={save} disabled={saving} className="bg-[#C9A84C] hover:bg-[#B89A3E] text-black rounded-full" data-testid="meta-save">
        {saving ? 'Zapisywanie...' : 'Zapisz i oznacz jako przejrzane'}
      </Button>
    </div>
  );
};
